import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from "framer-motion";
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { Pagination, Autoplay, Navigation } from 'swiper/modules';
import ContactSection from "../../components/ContactSection";

const ManagedServices = () => {

    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const features = [
        {
            id: '01',
            title: 'Managed Infrastructure & Compute',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2" /><path d="M8 21h8M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'Solaris manages your entire compute infrastructure - from physical servers and virtualisation platforms to cloud-hosted workloads. We handle capacity planning, patching, firmware updates, performance tuning, and incident resolution, freeing your internal teams to focus on strategic business priorities.',
            accent: '#2563eb'
        },
        {
            id: '02',
            title: 'Managed Network Operations',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="3" stroke="currentColor" strokeWidth="2" /><circle cx="5" cy="19" r="3" stroke="currentColor" strokeWidth="2" /><circle cx="19" cy="19" r="3" stroke="currentColor" strokeWidth="2" /><path d="M12 8v4M12 12l-5 4M12 12l5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            ),
            description: 'Our managed network services provide end-to-end oversight of your LAN, WAN, SD-WAN, and wireless environments. Solaris monitors network health in real time, responds to faults proactively, and optimises performance continuously - ensuring your connectivity infrastructure remains resilient and aligned to business demands.',
            accent: '#0ea5e9'
        },
        {
            id: '03',
            title: 'Managed Security Services',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
            ),
            description: 'Solaris delivers comprehensive managed security services including 24/7 threat monitoring, SIEM management, vulnerability scanning, endpoint protection, and incident response. We embed security operations into your daily IT management - reducing risk exposure and ensuring continuous compliance across your environment.',
            accent: '#6366f1'
        },
        {
            id: '04',
            title: 'Managed Cloud Services',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>
            ),
            description: 'From public cloud governance to private cloud operations, Solaris manages your multi-cloud environments with discipline and precision. We provide cloud cost optimisation, workload management, availability monitoring, and configuration governance - ensuring your cloud investments deliver maximum business value.',
            accent: '#f59e0b'
        },
        {
            id: '05',
            title: 'Service Desk & End-User Support',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'Solaris provides scalable service desk capabilities with multi-channel support across phone, email, and chat. Our ITSM-aligned service desk delivers efficient incident management, request fulfilment, and problem resolution - ensuring your workforce receives timely, high-quality support regardless of location or device.',
            accent: '#10b981'
        },
        {
            id: '06',
            title: 'ITSM & Process Governance',
            icon: (
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            ),
            description: 'We implement and operate ITSM frameworks - including ITIL-aligned change management, configuration management, and service level governance - that bring structure, accountability, and measurable outcomes to your IT operations. Our approach ensures consistent service delivery and continuous improvement across your managed environment.',
            accent: '#ef4444'
        }
    ];

    const servicesSpectrum = [
        'Managed Infrastructure & Compute',
        'Managed Network Operations',
        'Managed Security Services',
        'Managed Cloud & Multi-Cloud',
        'Service Desk & End-User Support',
        'ITSM & Process Governance',
        'Patch & Vulnerability Management',
        'Backup & Disaster Recovery',
        'Performance Monitoring & Reporting',
        'Vendor & Contract Management'
    ];

    const caseStudies = [
        {
            tag: 'Managed Services',
            title: 'Network Modernisation And Infrastructure Transformation Programs',
            image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Managed Services',
            title: 'Explore How Enterprise IT Modernisation Drove Operational Efficiency',
            image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&auto=format&fit=crop&q=80',
            dark: false
        },
        {
            tag: 'Managed Services',
            title: 'End-to-End Managed Operations Reducing IT Overhead by 40% for a Financial Institution',
            image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&auto=format&fit=crop&q=80',
            dark: true
        },
        {
            tag: 'Managed Services',
            title: '24/7 Managed Security Operations Deployed Across Multi-Site Enterprise Environment',
            image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600&auto=format&fit=crop&q=80',
            dark: false
        }
    ];

    const faqs = [
        {
            question: 'How does Solaris design and implement managed services?',
            answer: 'Solaris follows a structured onboarding methodology for every managed services engagement. We begin with a thorough discovery of your current IT environment, service requirements, and business objectives - then design a tailored managed service model with clearly defined SLAs, escalation paths, and reporting cadences. Our certified engineers handle full transition, knowledge transfer, and steady-state operations.'
        },
        {
            question: 'Do you support hybrid and hyperconverged environments?',
            answer: 'Yes. Solaris has deep expertise managing hybrid IT environments that span on-premises infrastructure, private cloud, and public cloud platforms. We support hyperconverged deployments from vendors including Nutanix, VMware vSAN, and Cisco HyperFlex - providing unified management and monitoring across your entire environment from a single operations team.'
        },
        {
            question: 'How do you ensure business continuity and disaster recovery?',
            answer: 'Business continuity is embedded in every Solaris managed service engagement. We design and operate disaster recovery capabilities including automated backup, offsite replication, and tested recovery runbooks - all aligned to your specific RPO and RTO requirements. Our 24/7 operations team monitors for availability issues and initiates failover procedures without delay when required.'
        },
        {
            question: 'What monitoring and observability solutions do you offer?',
            answer: 'Solaris deploys comprehensive monitoring across infrastructure, networks, applications, and security layers. Our operations teams use full stack observability platforms to deliver real-time alerting, performance dashboards, and detailed monthly reporting - giving you complete visibility into your IT environment and the intelligence to make informed decisions.'
        },
        {
            question: 'Do you provide post-deployment support and optimisation?',
            answer: 'Ongoing optimisation is a core component of all Solaris managed service agreements. We conduct regular service reviews, capacity assessments, and continual improvement initiatives - ensuring your IT environment evolves in line with your business. Our teams proactively identify inefficiencies and recommend enhancements before they impact performance or cost.'
        },
        {
            question: 'What ITSM frameworks do your managed services align to?',
            answer: 'Solaris managed services are aligned to ITIL best practices, covering incident management, problem management, change management, and service level management. We leverage industry-leading ITSM platforms to manage workflows, track SLAs, and provide transparent reporting - ensuring your IT operations are governed with rigour, consistency, and accountability.'
        }
    ];

    return (
        <>
            {/* ── HERO BANNER ── */}
            <section style={{
                position: 'relative',
                width: '100%',
                minHeight: '480px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,20,50,0.72)' }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '120px 15px 80px', textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 style={{
                            color: '#fff', fontWeight: 700,
                            fontSize: 'clamp(2rem, 4.5vw, 3.5rem)',
                            lineHeight: 1.2, margin: '0 auto 20px',
                            maxWidth: '820px'
                        }}>
                            Simplifying IT Operations Through Intelligent Managed Services
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.78)', fontSize: '1rem',
                            lineHeight: 1.75, maxWidth: '520px', margin: '0 auto 36px'
                        }}>
                            Proactive, people-first managed IT services that reduce complexity, improve performance, and free your teams to focus on what matters most
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <a href="#ms-overview" style={{ textDecoration: 'none' }}>
                                <button style={{
                                    display: 'inline-flex', alignItems: 'center',
                                    background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                    color: '#fff', border: 'none',
                                    padding: '14px 8px 14px 28px',
                                    borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                    cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    boxShadow: '0 8px 24px rgba(37,99,235,0.4)'
                                }}
                                    onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                                >
                                    <span style={{ marginRight: '16px' }}>Know More</span>
                                    <span style={{
                                        background: '#fff', borderRadius: '50%',
                                        width: '38px', height: '38px',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                </button>
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── WHAT IS MANAGED SERVICES ── */}
            <section id="ms-overview" style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div className="row align-items-center g-5">
                        <div className="col-lg-6">
                            <div style={{ position: 'relative' }}>
                                <img
                                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&auto=format&fit=crop&q=80"
                                    alt="Managed Services Team"
                                    style={{ width: '100%', borderRadius: '16px', objectFit: 'cover', height: '400px', display: 'block' }}
                                />
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <p style={{
                                color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                WHAT IS MANAGED SERVICES?
                            </p>
                            <h2 style={{
                                fontSize: 'clamp(1.9rem, 3vw, 2rem)', fontWeight: 700,
                                color: '#0f172a', lineHeight: 1.25, marginBottom: '28px', textAlign: 'left'
                            }}>
                                Simplifying IT Operations Through Intelligent Managed Services
                            </h2>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '20px', textAlign: 'left' }}>
                                Solaris Managed Services help enterprises optimise IT performance, reduce downtime, and improve governance. We manage complex, multi-vendor environments through standardised ITSM frameworks, automation, and 24/7 monitoring.
                            </p>
                            <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.85, marginBottom: '32px', textAlign: 'left' }}>
                                Our approach combines <strong style={{ color: '#0f172a' }}>technical expertise with human-first service delivery</strong> - ensuring predictable outcomes, faster resolution, and long-term operational stability across your entire IT estate.
                            </p>
                            <Link to="/contact" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)', color: '#fff',
                                padding: '13px 28px', borderRadius: '8px', fontWeight: 700,
                                fontSize: '0.9rem', textDecoration: 'none',
                                boxShadow: '0 6px 20px rgba(37,99,235,0.3)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(37,99,235,0.45)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,0.3)'; }}
                            >
                                Discuss Your Managed Services Requirements
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── SOLUTIONS GRID ── */}
            <section style={{ padding: '110px 0', background: '#0b1120', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', top: '-120px', left: '-120px',
                    width: '500px', height: '500px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(37,99,235,0.18) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', bottom: '-100px', right: '-100px',
                    width: '440px', height: '440px', borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(14,165,233,0.14) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.15)', border: '1px solid rgba(37,99,235,0.3)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '24px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Core Offerings
                            </span>
                        </div>
                        <h2 style={{
                            fontSize: 'clamp(2rem, 3vw, 2rem)', fontWeight: 600,
                            color: '#fff', lineHeight: 1.2, marginBottom: '20px'
                        }}>
                            Comprehensive{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Managed Services Capabilities
                            </span>
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
                            End-to-end managed IT expertise covering infrastructure, networks, security, cloud, and service operations - all under one team.
                        </p>
                    </div>

                    <div className="row g-4">
                        {features.map((f, i) => (
                            <div key={f.id} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    viewport={{ once: true }}
                                    whileHover={{ y: -6 }}
                                    style={{
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.09)',
                                        borderRadius: '16px', padding: '36px 30px',
                                        height: '100%', cursor: 'default',
                                        transition: 'border-color 0.3s, background 0.3s',
                                        position: 'relative', overflow: 'hidden'
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.borderColor = f.accent + '55'; e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                                >
                                    <div style={{
                                        position: 'absolute', top: 0, left: '30px', right: '30px',
                                        height: '2px', borderRadius: '0 0 4px 4px',
                                        background: `linear-gradient(90deg, ${f.accent}, transparent)`
                                    }} />
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: f.accent + '20', border: `1px solid ${f.accent}40`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: f.accent, marginBottom: '22px'
                                    }}>
                                        {f.icon}
                                    </div>
                                    <div style={{
                                        fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)',
                                        letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px'
                                    }}>{f.id}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '14px', lineHeight: 1.4 }}>{f.title}</h3>
                                    <p style={{ color: 'rgba(255,255,255,0.48)', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: 0 }}>{f.description}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SERVICES SPECTRUM ── */}
            <section style={{ position: 'relative', overflow: 'hidden', background: '#0f172a', padding: '100px 0' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&auto=format&fit=crop&q=80')`,
                    backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.18
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div className="row align-items-center g-5">
                        <div className="col-lg-5">
                            <p style={{
                                color: '#38bdf8', fontWeight: 700, letterSpacing: '3px',
                                textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '16px'
                            }}>
                                SERVICES SPECTRUM
                            </p>
                            <h2 style={{
                                color: '#fff', fontWeight: 800,
                                fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)',
                                lineHeight: 1.25, marginBottom: '20px'
                            }}>
                                The Complete Spectrum of Data Center Services
                            </h2>
                            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.85 }}>
                                A comprehensive range of managed IT services designed to keep your infrastructure, operations, and teams performing at their best - from routine management through to strategic transformation.
                            </p>
                        </div>
                        <div className="col-lg-7">
                            <div className="row g-3">
                                {servicesSpectrum.map((svc, i) => (
                                    <div key={i} className="col-md-6">
                                        <motion.div
                                            initial={{ opacity: 0, x: 20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.4, delay: i * 0.07 }}
                                            viewport={{ once: true }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '14px',
                                                padding: '16px 20px', borderRadius: '10px',
                                                background: 'rgba(255,255,255,0.06)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                backdropFilter: 'blur(10px)',
                                                transition: 'background 0.2s'
                                            }}
                                            onMouseOver={e => e.currentTarget.style.background = 'rgba(56,189,248,0.1)'}
                                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                                                <path d="M5 13L9 17L19 7" stroke="#38bdf8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: '0.95rem', fontWeight: 500 }}>{svc}</span>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHY CHOOSE SOLARIS ── */}
            <section style={{ padding: '90px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            WHY SOLARIS
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '18px' }}>
                            Trusted Across Industries
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1.05rem', maxWidth: '580px', margin: '0 auto' }}>
                            Delivering managed IT services with precision, accountability, and a relentless focus on business outcomes.
                        </p>
                    </div>

                    <div className="row g-4">
                        {[
                            { icon: '🏗️', title: 'Service-Led Design', desc: 'Every managed service engagement begins with a thorough discovery of your IT environment, business priorities, and SLA requirements - resulting in a bespoke service model, not a generic package.' },
                            { icon: '🔒', title: 'Security Embedded by Default', desc: 'Security operations, patch management, and compliance monitoring are woven into every managed service - not sold as optional add-ons.' },
                            { icon: '🤝', title: 'Vendor-Agnostic Expertise', desc: 'Certified across Cisco, Microsoft, HPE, Dell, AWS, Azure, and more - we manage your full multi-vendor environment from a single, accountable team.' },
                            { icon: '⏱️', title: '24/7 Operations Centre', desc: 'Our around-the-clock operations team monitors, responds, and resolves issues before they escalate - keeping your IT environment available and performing.' },
                            { icon: '⚡', title: 'ITIL-Aligned Processes', desc: 'Structured service management frameworks ensure consistent incident handling, change governance, and problem resolution - delivering reliability your business can depend on.' },
                            { icon: '📊', title: 'Transparent Reporting', desc: 'Monthly service reviews, SLA performance dashboards, and actionable insights keep you fully informed and in control of your managed IT environment at all times.' }
                        ].map((item, i) => (
                            <div key={i} className="col-lg-4 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '32px 28px', borderRadius: '16px',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        height: '100%', cursor: 'default'
                                    }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(37,99,235,0.1)', borderColor: '#93c5fd' }}
                                >
                                    <div style={{
                                        width: '56px', height: '56px', borderRadius: '14px',
                                        background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '1.6rem', marginBottom: '20px'
                                    }}>{item.icon}</div>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>{item.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.75, marginBottom: 0 }}>{item.desc}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── CASE STUDIES ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            CASE STUDIES
                        </p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', marginBottom: '0' }}>
                            How Our Customers<br />Achieved Success
                        </h2>
                    </div>

                    <div style={{ position: 'relative' }}>
                        <Swiper
                            modules={[Navigation, Autoplay, Pagination]}
                            spaceBetween={24}
                            slidesPerView={1}
                            navigation={{ prevEl: '.cs-ms-prev-btn', nextEl: '.cs-ms-next-btn' }}
                            pagination={{ clickable: true, el: '.cs-ms-pagination' }}
                            autoplay={{ delay: 4000, disableOnInteraction: false }}
                            breakpoints={{ 768: { slidesPerView: 2 }, 1200: { slidesPerView: 3 } }}
                            className="cs-ms-swiper pb-5"
                        >
                            {caseStudies.map((cs, i) => (
                                <SwiperSlide key={i}>
                                    <motion.div
                                        style={{
                                            borderRadius: '14px', overflow: 'hidden',
                                            background: '#fff', border: '1px solid #e2e8f0',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                                        }}
                                        whileHover={{ y: -6, boxShadow: '0 20px 50px rgba(0,0,0,0.12)' }}
                                    >
                                        <div style={{ position: 'relative', height: '220px', overflow: 'hidden' }}>
                                            <img src={cs.image} alt={cs.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            <div style={{
                                                position: 'absolute', inset: 0,
                                                background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6) 100%)'
                                            }} />
                                            {cs.dark && (
                                                <div style={{
                                                    position: 'absolute', bottom: '16px', left: '16px', right: '16px',
                                                    color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.4
                                                }}>
                                                    Discover how we transformed video collaboration at scale
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ padding: '24px' }}>
                                            <span style={{
                                                fontSize: '0.75rem', fontWeight: 700, color: '#2563eb',
                                                textTransform: 'uppercase', letterSpacing: '1px',
                                                display: 'block', marginBottom: '10px'
                                            }}>+ {cs.tag}</span>
                                            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.5, marginBottom: '20px' }}>{cs.title}</h4>
                                            <Link to="/case-study" style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                background: '#fff', color: '#2563eb',
                                                border: '1.5px solid #2563eb', padding: '9px 20px',
                                                borderRadius: '8px', fontSize: '0.85rem', fontWeight: 700,
                                                textDecoration: 'none', transition: 'background 0.2s, color 0.2s'
                                            }}
                                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2563eb'; }}
                                            >
                                                Read More
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                            </Link>
                                        </div>
                                    </motion.div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '20px' }}>
                            <button className="cs-ms-prev-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#475569', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                            <div className="cs-ms-pagination" style={{ display: 'flex', gap: '6px' }} />
                            <button className="cs-ms-next-btn" style={{
                                width: '40px', height: '40px', borderRadius: '50%',
                                border: '1.5px solid #2563eb', background: '#2563eb', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#fff', transition: 'all 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.background = '#1d4ed8'; e.currentTarget.style.borderColor = '#1d4ed8'; }}
                                onMouseOut={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </button>
                        </div>
                    </div>
                </div>

                <style>{`
                    .cs-ms-swiper .swiper-pagination-bullet {
                        width: 8px; height: 8px; background: #cbd5e1; opacity: 1;
                        border-radius: 50%; margin: 0 4px; transition: all 0.3s;
                    }
                    .cs-ms-swiper .swiper-pagination-bullet-active {
                        background: #2563eb; width: 24px; border-radius: 4px;
                    }
                `}</style>
            </section>

            {/* ── FAQ SECTION ── */}
            <section style={{ padding: '100px 0', background: '#0a0f1e', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'radial-gradient(ellipse at 20% 50%, rgba(37,99,235,0.08) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(99,102,241,0.08) 0%, transparent 60%)',
                    pointerEvents: 'none'
                }} />

                <div className="container" style={{ position: 'relative', zIndex: 2 }}>
                    <div style={{ textAlign: 'center', marginBottom: '64px' }}>
                        <p style={{
                            color: '#60a5fa', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>
                            FREQUENTLY ASKED QUESTIONS
                        </p>
                        <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: '16px' }}>
                            What Would You Like to Know<br />About Managed Services
                        </h2>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '1rem' }}>
                            Everything you need to know about our services and solutions
                        </p>
                    </div>

                    <div className="row g-5 align-items-start">
                        <div className="col-lg-7">
                            {faqs.map((faq, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    viewport={{ once: true }}
                                    style={{ marginBottom: '12px' }}
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                        style={{
                                            width: '100%', textAlign: 'left',
                                            background: openFaq === i ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.04)',
                                            border: openFaq === i ? '1px solid rgba(99,102,241,0.4)' : '1px solid rgba(255,255,255,0.08)',
                                            borderRadius: openFaq === i ? '12px 12px 0 0' : '12px',
                                            padding: '20px 24px',
                                            display: 'flex', alignItems: 'center', gap: '16px',
                                            cursor: 'pointer', transition: 'all 0.25s'
                                        }}
                                    >
                                        <span style={{
                                            width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                                            background: openFaq === i ? 'linear-gradient(135deg,#6366f1,#4f46e5)' : 'rgba(255,255,255,0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.78rem', fontWeight: 700,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.25s'
                                        }}>
                                            {i + 1}
                                        </span>
                                        <span style={{
                                            flex: 1, fontSize: '0.98rem', fontWeight: 600,
                                            color: openFaq === i ? '#fff' : 'rgba(255,255,255,0.75)',
                                            lineHeight: 1.45, textAlign: 'left'
                                        }}>
                                            {faq.question}
                                        </span>
                                        <span style={{
                                            width: '24px', height: '24px', flexShrink: 0,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: openFaq === i ? '#a5b4fc' : 'rgba(255,255,255,0.4)',
                                            fontSize: '1.4rem', lineHeight: 1, fontWeight: 300,
                                            transition: 'color 0.25s'
                                        }}>
                                            {openFaq === i ? '−' : '+'}
                                        </span>
                                    </button>

                                    {openFaq === i && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: 'rgba(99,102,241,0.07)',
                                                border: '1px solid rgba(99,102,241,0.4)',
                                                borderTop: 'none',
                                                borderRadius: '0 0 12px 12px',
                                                padding: '20px 24px 24px 72px'
                                            }}
                                        >
                                            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', lineHeight: 1.8, margin: 0 }}>
                                                {faq.answer}
                                            </p>
                                        </motion.div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        <div className="col-lg-5">
                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                viewport={{ once: true }}
                                style={{
                                    borderRadius: '20px', overflow: 'hidden', position: 'relative',
                                    background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #6366f1 100%)',
                                    padding: '52px 40px', textAlign: 'center',
                                    minHeight: '360px', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center'
                                }}
                            >
                                <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '140px', height: '140px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '30px', left: '-20px', width: '90px', height: '90px', borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', top: '50px', left: '20px', width: '55px', height: '55px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
                                <div style={{ position: 'absolute', bottom: '-20px', right: '40px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', pointerEvents: 'none' }} />

                                <div style={{
                                    width: '64px', height: '64px', borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.15)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '28px', position: 'relative', zIndex: 1
                                }}>
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="white" strokeWidth="2" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3 style={{
                                    color: '#fff', fontWeight: 800,
                                    fontSize: 'clamp(1.4rem, 2.5vw, 1.75rem)',
                                    lineHeight: 1.3, marginBottom: '16px', position: 'relative', zIndex: 1
                                }}>
                                    Didn't Find What You Were Looking For?
                                </h3>
                                <p style={{
                                    color: 'rgba(255,255,255,0.7)', fontSize: '0.92rem',
                                    lineHeight: 1.7, marginBottom: '32px',
                                    position: 'relative', zIndex: 1, maxWidth: '280px'
                                }}>
                                    Our managed services team is ready to help with any questions or bespoke IT requirements you may have.
                                </p>
                                <Link to="/contact" style={{ textDecoration: 'none', position: 'relative', zIndex: 1 }}>
                                    <button style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                                        background: '#fff', color: '#4f46e5',
                                        border: 'none', padding: '14px 28px',
                                        borderRadius: '50px', fontWeight: 700, fontSize: '0.92rem',
                                        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.2)'
                                    }}
                                        onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.3)'; }}
                                        onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                                    >
                                        Get In Touch With Our Experts
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M5 12h14M12 5l7 7-7 7" stroke="#4f46e5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </Link>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default ManagedServices;
